import { Component, ViewChild, Output, EventEmitter, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SelectingEventArgs, TabComponent, TabModule } from '@syncfusion/ej2-angular-navigations';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { ToastService } from 'src/app/base/services/toast.service';
import { AssetImageEditorComponent } from 'src/app/domain/assets/shared/asset-image-editor/asset-image-editor.component';
import { TranslateModule } from '@ngx-translate/core';
import { AssetUploadEditorComponent } from '../asset-upload-editor/asset-upload-editor.component';
import { ProductAssetsService } from '../../product-assets/product-assets.service';
import { AssetUploadEditorFormType } from '../asset-upload-editor/asset-upload-editor-form-type';

@Component({
    selector: 'app-asset-edit-tab-group',
    templateUrl: './asset-edit-tab-group.component.html',
    styleUrl: './asset-edit-tab-group.component.scss',
    imports: [TabModule, AssetUploadEditorComponent, AssetImageEditorComponent, TranslateModule],
    providers: [ProductAssetsService]
})
export class AssetEditTabGroupComponent {

  @ViewChild('tabObj')
  public tabObj: TabComponent;

  @ViewChild("assetUploadEditorComponent")
  public assetUploadEditorComponent: AssetUploadEditorComponent;

  @ViewChild("assetImageEditorComponent")
  public assetImageEditorComponent: AssetImageEditorComponent;

  @Input()
  public assetUploadEditorForm: FormGroup<AssetUploadEditorFormType>;

  @Output()
  public setScreenSize: EventEmitter<boolean> = new EventEmitter<boolean>();

  public get fileData(): string | Blob {
    return this.assetUploadEditorForm.controls.intermediateFileInfo.value?.rawFile ?? this.assetUploadEditorForm.controls.fileLink.value ?? null;
  }

  public animation: object = {
    previous: { effect: "", duration: 0, easing: "" },
    next: { effect: "", duration: 0, easing: "" }
  };

  public isFullScreen: boolean = false;
  public showImageEditor: boolean = false;

  constructor(
    private toastService: ToastService,
  ) { }

  public tabGroupCreated() {
    this.setTabVisibility();
    this.assetUploadEditorForm.controls.fileExtension.valueChanges.subscribe((value) => {
      this.setTabVisibility();
      this.assetImageEditorComponent?.imageEditorObj?.update();
    });
  }

  public select(event: SelectingEventArgs): void {
    if (event.selectingIndex === 0) {
      if (this.assetImageEditorComponent?.hasPendingChanges()) {
        this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.title',
          'i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.message', null);
        event.cancel = true;
        return;
      }

      if (this.isFullScreen) {
        this.toggleFullScreen();
      }
      this.showImageEditor = false;
    }
    if (event.selectingIndex === 1) {
      this.showImageEditor = true;
    }

    if (event.isSwiped) {
      event.cancel = true;
      return;
    }
  }

  public toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
    this.setScreenSize.emit(this.isFullScreen);
  }

  public resetTabGroup() {
    this.isFullScreen = false;
    this.tabObj?.select(0);
    this.assetUploadEditorComponent.clearUploader();
  }

  public async getImageDataFromCanvas(): Promise<void> {
    // Save edited image
    var imageData = this.assetImageEditorComponent.imageEditorObj.getImageData();
    var canvas = document.createElement('CANVAS') as HTMLCanvasElement;
    canvas.height = imageData.height;
    canvas.width = imageData.width;
    var ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    var mimeType = MimeTypeHelper.getMimeType(this.assetUploadEditorForm.controls.fileExtension.value);

    if (this.assetUploadEditorForm.controls.intermediateFileInfo.value) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), mimeType);
      });

      if (blob) {
        // Update the rawFile property of the intermediateFileInfo control
        const intermediateFileInfo = this.assetUploadEditorForm.controls.intermediateFileInfo.value;
        intermediateFileInfo.rawFile = blob;
        this.assetUploadEditorForm.controls.intermediateFileInfo.setValue(intermediateFileInfo);
      }
    } else {
      var dataURL = canvas.toDataURL(MimeTypeHelper.getMimeType(this.assetUploadEditorForm.controls.fileExtension.value));
      this.assetUploadEditorForm.controls.base64FileData.setValue(dataURL);
    }
  }

  public canSave(): boolean {
    if (this.assetImageEditorComponent?.hasPendingChanges()) {
      this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.title',
        'i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.message', null);
      return false;
    }
    return true;
  }

  private setTabVisibility() {
    var extension = this.assetUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    this.tabObj?.hideTab(1, extension !== "jpg" &&
                            extension !== "jpeg" &&
                            extension !== "png");
  }

}
